import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	Anchor,
	DateString,
	DropOverActionList,
	IconButton,
	PersonAvatar,
	PipeSeparator
} from "client/Components";
import { actionComponentFactory, computeActionsToRender } from "client/Components/ActionList/actionsRenderHelpers";
import { TimeZone } from "client/DateAndTime/timezones";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { useToggle } from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	MouseEvent,
	ReactElement,
	RefObject,
	useMemo,
	useRef
} from "react";
import { HubAvatar } from "../HubAvatar/HubAvatar";
import { NameAndPronouns } from "../NameAndPronouns";
import styles from "./PostHeader.module.less";

export type PostHeaderActionContext = {
	canEdit: boolean;
};

export type PostHeaderActionsAndContext = {
	actions: readonly ActionAndOthers<PostHeaderActionContext>[];
	actionContext: PostHeaderActionContext;
};

export type PostHeaderProps = {
	onNavigateToCommunity?: () => void;
	authorUserInfo: UserIdentityModel | null;
	community: CommunityViewModel | undefined;
	publicationDate: string | Date;
	timezone: TimeZone;
	trackingId: string;
	isInsideClickableArea?: boolean;
} & (
	| PostHeaderActionsAndContext
	| {
		actions?: undefined;
		actionContext?: undefined;
	}
);

export const PostHeader = (props: PostHeaderProps): ReactElement => {
	const {
		onNavigateToCommunity,
		authorUserInfo,
		community,
		actions,
		actionContext,
		publicationDate,
		timezone,
		trackingId,
		isInsideClickableArea
	} = props;

	const [isMenuOpen, toggleIsMenuOpen] = useToggle(false);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const actionsToRender = useMemo(
		(): PostHeaderActionsAndContext | undefined => {
				if (!actions) {
					return undefined;
				}

				return {
					actions: computeActionsToRender(
						actions,
						actionContext
					),
					actionContext
				};
			},
		[actions, actionContext]
	);

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				<div className={styles.avatarAndInfo}>
					<AvatarAndInfo
						authorUserInfo={authorUserInfo}
						community={community}
						onNavigateToCommunity={onNavigateToCommunity}
						publicationDate={publicationDate}
						timezone={timezone}
						isInsideClickableArea={isInsideClickableArea}
					/>
				</div>
				{isNotNil(actionsToRender) && actionsToRender.actions.length > 0 && (
					<IconButton
						trackingId="more"
						icon="ellipsis"
						kind="transparent"
						onClick={() => toggleIsMenuOpen()}
						ref={menuButtonRef}
						shape="round"
						isActive={isMenuOpen}
					/>
				)}
			</div>
			{isNotNil(actionsToRender) && isMenuOpen && (
				<DropOverActionList
					trackingId={generateTrackingId(trackingId, "__actions-dropover")}
					target={ensureNotNil(menuButtonRef.current)}
					targetAlignment="bl"
					contentAlignment="tl"
					onClose={toggleIsMenuOpen}
					menuTitle={localize("_Post")}
				>
					{actionsToRender.actions.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, actionsToRender.actionContext)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};
PostHeader.displayName="PostHeader";

type AvatarAndInfoProps = {
	authorUserInfo: UserIdentityModel | null;
	community: CommunityViewModel | undefined;
	onNavigateToCommunity?: () => void;
	publicationDate: string | Date;
	timezone: TimeZone;
	isInsideClickableArea?: boolean;
};

const AvatarAndInfo = (props: AvatarAndInfoProps): ReactElement => {
	const {
		authorUserInfo,
		community,
		onNavigateToCommunity,
		publicationDate,
		timezone,
		isInsideClickableArea = false
	} = props;

	if (isNil(authorUserInfo)) {
		const initials: string = getComputedDisplayName({
			displayName: localize("_DeletedUser")
		}).initials;

		return (
			<>
				<PersonAvatar
					trackingId="deleted-user-avatar"
					initials={initials}
				/>
				<div className={styles.nameAndDateContainer}>
					<div className={styles.deletedUserName}>
						{localize("_DeletedUser")}
					</div>
					<CommunityAndDate
						community={community}
						onNavigateToCommunity={onNavigateToCommunity}
						publicationDate={publicationDate}
						timezone={timezone}
					/>
				</div>
			</>
		);
	}

	return (
		<>
			<HubAvatar
				authorUserInfo={authorUserInfo}
				shouldShowContactDetails
				shouldBubbleClick={!isInsideClickableArea}
			/>
			<div className={styles.nameAndDateContainer}>
				<NameAndPronouns
					authorUserInfo={authorUserInfo}
				/>
				<CommunityAndDate
					community={community}
					onNavigateToCommunity={onNavigateToCommunity}
					publicationDate={publicationDate}
					timezone={timezone}
				/>
			</div>
		</>
	);
};
AvatarAndInfo.displayName = "AvatarAndInfo";

type CommunityAndDateProps = {
	community: CommunityViewModel | undefined;
	onNavigateToCommunity?: () => void;
	publicationDate: string | Date;
	timezone: TimeZone;
};

const CommunityAndDate = (props: CommunityAndDateProps): ReactElement => {
	const {
		community,
		onNavigateToCommunity,
		publicationDate,
		timezone
	} = props;

	const communityClickHandler: ((args: MouseEvent<HTMLAnchorElement>) => void) | undefined = isNil(onNavigateToCommunity)
		? undefined
		: (args: MouseEvent<HTMLAnchorElement>): void => {
			args.preventDefault();
			args.stopPropagation();
			onNavigateToCommunity();
		};

	return (
		<div className={styles.communityAndDate}>
			{isNotNil(community) && (
				<>
					<span
						{...setTrackingId("community-name")}
						className={styles.communityName}
					>
						{isNotNil(communityClickHandler)
							? (
								<Anchor
									onClick={communityClickHandler}
									className={styles.communityLink}
								>
									{community.name}
								</Anchor>
							)
							: (
								<>
									{community.name}
								</>
							)
						}
					</span>
					<PipeSeparator />
				</>
			)}
			<span
				{...setTrackingId("timestamp")}
				className={styles.date}
			>
				<DateString
					value={publicationDate}
					format="RelativeTimestamp"
					timezone={timezone}
				/>
			</span>
		</div>
	);
};
CommunityAndDate.displayName = "CommunityAndDate";